/**
 * Admin Controls Store — Owner can timeout admins or grant conflict bypass.
 * Stored in localStorage under khays_admin_controls and synced to cloud.
 */

export interface AdminControl {
  timedOut?: boolean;
  timedOutUntil?: number; // epoch ms, 0 = indefinite
  bypassConflicts?: boolean;
  otpExempt?: boolean;
  otpExemptUntil?: number; // epoch ms, 0 = permanent
  otpExemptReason?: string;
  otpExemptGrantedAt?: number;
  otpExemptGrantedBy?: string;
  updatedAt?: number;
  lastSeen?: number; // epoch ms — tracks when admin was last active
}

/** Global owner setting: enable/disable conflict detection for all admins */
const CONFLICT_TOGGLE_KEY = "khays_conflict_detection_enabled";

export function isConflictDetectionEnabled(): boolean {
  try {
    const v = localStorage.getItem(CONFLICT_TOGGLE_KEY);
    if (v !== null) return JSON.parse(v);
  } catch {}
  return true; // enabled by default
}

export function setConflictDetectionEnabled(enabled: boolean): void {
  localStorage.setItem(CONFLICT_TOGGLE_KEY, JSON.stringify(enabled));
}

export type AdminControlsMap = Record<string, AdminControl>; // keyed by user_id

const KEY = "khays_admin_controls";

export function loadAdminControls(): AdminControlsMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

export function saveAdminControls(controls: AdminControlsMap): void {
  localStorage.setItem(KEY, JSON.stringify(controls));
}

export function isAdminTimedOut(userId: string): boolean {
  const controls = loadAdminControls();
  const c = controls[userId];
  if (!c?.timedOut) return false;
  // Check expiry
  if (c.timedOutUntil && c.timedOutUntil > 0 && Date.now() > c.timedOutUntil) {
    // Expired — auto-clear
    delete controls[userId]?.timedOut;
    delete controls[userId]?.timedOutUntil;
    saveAdminControls(controls);
    return false;
  }
  return true;
}

export function hasConflictBypass(userId: string): boolean {
  const controls = loadAdminControls();
  return !!controls[userId]?.bypassConflicts;
}

export function setAdminTimeout(userId: string, timedOut: boolean, durationMs?: number): void {
  const controls = loadAdminControls();
  if (!controls[userId]) controls[userId] = {};
  controls[userId].timedOut = timedOut;
  controls[userId].timedOutUntil = timedOut && durationMs ? Date.now() + durationMs : 0;
  controls[userId].updatedAt = Date.now();
  if (!timedOut) {
    delete controls[userId].timedOut;
    delete controls[userId].timedOutUntil;
  }
  saveAdminControls(controls);
}

export function setConflictBypass(userId: string, bypass: boolean): void {
  const controls = loadAdminControls();
  if (!controls[userId]) controls[userId] = {};
  controls[userId].bypassConflicts = bypass;
  controls[userId].updatedAt = Date.now();
  saveAdminControls(controls);
}

/** Check if a user is currently exempt from the staff OTP gate. Auto-clears expired exemptions. */
export function isOtpExempt(userId: string): boolean {
  const controls = loadAdminControls();
  const c = controls[userId];
  if (!c?.otpExempt) return false;
  if (c.otpExemptUntil && c.otpExemptUntil > 0 && Date.now() > c.otpExemptUntil) {
    delete controls[userId].otpExempt;
    delete controls[userId].otpExemptUntil;
    saveAdminControls(controls);
    return false;
  }
  return true;
}

/** Returns expiry timestamp (ms) or 0 for permanent, or null if not exempt. */
export function getOtpExemptUntil(userId: string): number | null {
  const controls = loadAdminControls();
  const c = controls[userId];
  if (!c?.otpExempt) return null;
  return c.otpExemptUntil ?? 0;
}

export function setOtpExempt(
  userId: string,
  exempt: boolean,
  durationMs?: number,
  meta?: { reason?: string; grantedBy?: string }
): void {
  const controls = loadAdminControls();
  if (!controls[userId]) controls[userId] = {};
  if (exempt) {
    controls[userId].otpExempt = true;
    controls[userId].otpExemptUntil = durationMs && durationMs > 0 ? Date.now() + durationMs : 0;
    controls[userId].otpExemptReason = meta?.reason?.trim() || undefined;
    controls[userId].otpExemptGrantedAt = Date.now();
    controls[userId].otpExemptGrantedBy = meta?.grantedBy || undefined;
  } else {
    delete controls[userId].otpExempt;
    delete controls[userId].otpExemptUntil;
    delete controls[userId].otpExemptReason;
    delete controls[userId].otpExemptGrantedAt;
    delete controls[userId].otpExemptGrantedBy;
  }
  controls[userId].updatedAt = Date.now();
  saveAdminControls(controls);
}

export function getOtpExemptInfo(userId: string): {
  status: "none" | "active" | "permanent" | "expired";
  until: number; // 0 for permanent, epoch ms otherwise, negative when expired
  remainingMs: number; // Infinity for permanent, 0 if none/expired
  reason?: string;
  grantedAt?: number;
  grantedBy?: string;
} {
  const controls = loadAdminControls();
  const c = controls[userId];
  if (!c?.otpExempt) return { status: "none", until: 0, remainingMs: 0 };
  const until = c.otpExemptUntil ?? 0;
  if (until === 0) {
    return {
      status: "permanent",
      until: 0,
      remainingMs: Infinity,
      reason: c.otpExemptReason,
      grantedAt: c.otpExemptGrantedAt,
      grantedBy: c.otpExemptGrantedBy,
    };
  }
  const remaining = until - Date.now();
  return {
    status: remaining > 0 ? "active" : "expired",
    until,
    remainingMs: Math.max(0, remaining),
    reason: c.otpExemptReason,
    grantedAt: c.otpExemptGrantedAt,
    grantedBy: c.otpExemptGrantedBy,
  };
}

export function formatRemainingDuration(ms: number): string {
  if (!isFinite(ms)) return "permanent";
  if (ms <= 0) return "expired";
  const sec = Math.floor(ms / 1000);
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  if (m > 0) return `${m}m left`;
  return "<1m left";
}

/** Update last-seen timestamp for a user */
export function updateLastSeen(userId: string): void {
  const controls = loadAdminControls();
  if (!controls[userId]) controls[userId] = {};
  controls[userId].lastSeen = Date.now();
  saveAdminControls(controls);
}

/** Check if user was active in the last N ms (default 5 minutes) */
export function isAdminOnline(userId: string, thresholdMs = 5 * 60 * 1000): boolean {
  const controls = loadAdminControls();
  const lastSeen = controls[userId]?.lastSeen;
  if (!lastSeen) return false;
  return Date.now() - lastSeen < thresholdMs;
}

export const TIMEOUT_DURATIONS = [
  { label: "15 minutes", ms: 15 * 60 * 1000 },
  { label: "30 minutes", ms: 30 * 60 * 1000 },
  { label: "1 hour", ms: 60 * 60 * 1000 },
  { label: "2 hours", ms: 2 * 60 * 60 * 1000 },
  { label: "4 hours", ms: 4 * 60 * 60 * 1000 },
  { label: "Until manually lifted", ms: 0 },
] as const;

export const OTP_EXEMPT_DURATIONS = [
  { label: "1 hour", ms: 60 * 60 * 1000 },
  { label: "8 hours", ms: 8 * 60 * 60 * 1000 },
  { label: "24 hours", ms: 24 * 60 * 60 * 1000 },
  { label: "7 days", ms: 7 * 24 * 60 * 60 * 1000 },
  { label: "30 days", ms: 30 * 24 * 60 * 60 * 1000 },
  { label: "Permanent (until revoked)", ms: 0 },
] as const;
